/**
 * useKeyboardShortcuts Hook
 * Handles keyboard shortcuts for undo, redo, delete, and arrow key nudging
 */

import { useEffect } from 'react'
import { dia } from '@joint/core'

export function useKeyboardShortcuts(
  selectedElementId: string | null,
  selectedElementIds: string[],
  graph: dia.Graph | null,
  undo: () => void,
  redo: () => void,
  onElementDelete?: (elementId: string) => void,
  onElementCountChange?: (count: number) => void,
  disabled?: boolean,
  onGroup?: () => void,
  onUngroup?: () => void,
  onCopy?: () => void,
  onPaste?: () => void
) {
  useEffect(() => {
    // Don't register shortcuts if disabled (e.g., in viewer mode)
    if (disabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts when typing in input fields
      const target = e.target as HTMLElement
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return
      }
      // Group: Shift+G (Ctrl/Cmd 없이 Shift 단독)
      // 한글 IME 상태에서 G 키를 누르면 e.key 가 'G'/'g' 가 아닌 'ㅎ'/'Process' 로
      // 들어오기 때문에, IME-독립 물리 키 식별자인 e.code === 'KeyG' 를 사용한다.
      // e.key 는 fallback (브라우저/플랫폼 호환).
      const isGKey = e.code === 'KeyG' || e.key === 'G' || e.key === 'g'
      if (
        isGKey &&
        e.shiftKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault()
        onGroup?.()
        return
      }
      // Ungroup: Ctrl+Shift+G (or Cmd+Shift+G on Mac)
      if (isGKey && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault()
        onUngroup?.()
        return
      }
      // Copy: Ctrl+C (or Cmd+C on Mac)
      // 한글 IME 켜진 상태에서 e.key 가 'ㅊ'/'Process' 로 들어와도 e.code 가
      // IME-독립이라 'KeyC' 그대로 보장됨. G 키 핸들러와 동일한 패턴.
      const isCKey = e.code === 'KeyC' || e.key === 'c' || e.key === 'C'
      if ((e.ctrlKey || e.metaKey) && isCKey && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        onCopy?.()
        return
      }
      // Paste: Ctrl+V (or Cmd+V on Mac)
      const isVKey = e.code === 'KeyV' || e.key === 'v' || e.key === 'V'
      if ((e.ctrlKey || e.metaKey) && isVKey && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        onPaste?.()
        return
      }
      // Undo: Ctrl+Z (or Cmd+Z on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      }
      // Redo: Ctrl+Y or Ctrl+Shift+Z (or Cmd+Shift+Z on Mac)
      else if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        e.preventDefault()
        redo()
      }
      // Delete: Delete or Backspace key
      else if ((e.key === 'Delete' || e.key === 'Backspace') && (selectedElementIds.length > 0 || selectedElementId)) {
        e.preventDefault()
        if (graph) {
          const idsToDelete = selectedElementIds.length > 1
            ? selectedElementIds
            : selectedElementId ? [selectedElementId] : []

          idsToDelete.forEach(id => {
            const element = graph.getCell(id)
            if (element) {
              element.remove()
              onElementDelete?.(id)
            }
          })

          if (idsToDelete.length > 0 && onElementCountChange) {
            onElementCountChange(graph.getElements().length)
          }
        }
      }
      // Arrow keys: Nudge selected element
      else if (
        (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
        selectedElementId &&
        graph
      ) {
        e.preventDefault()

        const element = graph.getCell(selectedElementId)
        if (!element || !element.isElement()) return

        const currentPos = element.position()
        const delta = e.shiftKey ? 10 : 1 // Shift key = 10px, normal = 1px
        let newX = currentPos.x
        let newY = currentPos.y

        switch (e.key) {
          case 'ArrowLeft':
            newX -= delta
            break
          case 'ArrowRight':
            newX += delta
            break
          case 'ArrowUp':
            newY -= delta
            break
          case 'ArrowDown':
            newY += delta
            break
        }

        element.position(newX, newY)

      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedElementId, selectedElementIds, graph, undo, redo, onElementDelete, onElementCountChange, disabled, onGroup, onUngroup, onCopy, onPaste])
}
